import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { api, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FindoSpinner } from "@/components/FindoSpinner";

export const SOURCE_COLORS = {
  subscriptions: "#1E2A6E", b2c_declaration: "#6366F1", finder_fees: "#00C896", recovery: "#F59E0B", shipping: "#14B8A6",
  boost_b2b: "#F43F5E", boost_b2c: "#FB923C", other: "#94A3B8",
};
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €`;
const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });

function ChartTooltip({ active, payload, label, labels }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="rounded-xl border border-border bg-white px-3 py-2 shadow-lg text-xs" data-testid="revenue-chart-tooltip">
      <p className="font-bold text-slate-800 mb-1">{monthLabel(label)}</p>
      {[...payload].reverse().filter((p) => p.value).map((p) => (
        <p key={p.dataKey} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />{labels[p.dataKey]}</span>
          <span className="font-semibold">{eur(p.value)}</span>
        </p>
      ))}
      <p className="mt-1 pt-1 border-t border-border flex justify-between font-bold"><span>Total</span><span>{eur(total)}</span></p>
    </div>
  );
}

export default function RevenueMonthlyChart() {
  const year = new Date().getFullYear();
  const [period, setPeriod] = useState("last12");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const periods = [["last12", "12 derniers mois"], [String(year), String(year)], [String(year - 1), String(year - 1)]];

  useEffect(() => {
    api(`/admin/revenue/monthly?period=${period}`).then(setData).catch((e) => toast.error(e.message));
  }, [period]);

  async function exportCsv() {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/revenue/monthly.csv?period=${period}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = `findloop-revenus-${period}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export CSV indisponible"); } finally { setBusy(false); }
  }

  return (
    <section className="bg-white rounded-2xl border border-border card-shadow p-5 mb-6" data-testid="revenue-monthly-chart">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-lg text-findo-navy">Revenus mensuels par source</h3>
          <p className="text-sm text-slate-500">Paiements encaissés, mois par mois.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1" data-testid="revenue-period">
            {periods.map(([k, l]) => (
              <button key={k} onClick={() => setPeriod(k)} data-testid={`revenue-period-${k}`}
                className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors ${period === k ? "bg-findo-navy text-white" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
            ))}
          </div>
          <Button variant="outline" onClick={exportCsv} disabled={busy} className="rounded-full font-semibold" data-testid="revenue-export-csv-btn">
            {busy ? <FindoSpinner size={15} className="mr-1.5" /> : <Download size={15} className="mr-1.5" />} Exporter CSV
          </Button>
        </div>
      </div>
      <div className="mt-4 h-80">
        {data && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis tickFormatter={(v) => `${v} €`} tick={{ fontSize: 12, fill: "#64748b" }} width={70} />
              <Tooltip content={<ChartTooltip labels={data.labels} />} cursor={{ fill: "rgba(148,163,184,0.12)" }} />
              {data.sources.map((s) => (
                <Bar key={s} dataKey={s} stackId="rev" fill={SOURCE_COLORS[s]} maxBarSize={44} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      {data && (
        <div className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-2" data-testid="revenue-chart-legend">
          {data.sources.map((s) => (
            <span key={s} className="flex items-center gap-1.5 text-sm font-medium text-slate-700" data-testid={`revenue-legend-${s}`}>
              <span className="h-3 w-3 rounded-sm" style={{ background: SOURCE_COLORS[s] }} />{data.labels[s]}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
